import { EventEmitter } from 'events';
import { OptionalUnaryArgType } from 'app/utils/types';

export type EventHandlerMap<T> = { readonly [K in keyof T]?: (value: T[K]) => void };
export type ExtractEventHandlerMap<Emitter> =
  Emitter extends { bindEvents(eventHandlerMap: infer A): void } ? A : never;

// Any object may be used as event handler map. This helper makes it
// explicit when this transformation happens.
export type AsEventMap<T> = {
  [K in keyof T]: OptionalUnaryArgType<T[K]>
};

export default class EventMapEmitter<EventMap extends { [name: string]: any }> {
  private readonly emitter: EventEmitter;

  public constructor() {
    this.emitter = new EventEmitter();
    this.emitter.setMaxListeners(Infinity);
  }

  public emit<
    Event extends Extract<keyof EventMap, string>,
    Value extends EventMap[Event]
  >(event: Event, value: Value) {
    this.emitter.emit(event, value);
  }

  public bindEvents(map: EventHandlerMap<EventMap>) {
    for (const name in map) {
      if (map.hasOwnProperty(name)) {
        const handler = map[name];
        if (handler instanceof Function) {
          this.emitter.addListener(name, handler);
        }
      }
    }
  }

  public unbindEvents(map: EventHandlerMap<EventMap>) {
    for (const name in map) {
      if (map.hasOwnProperty(name)) {
        const handler = map[name];
        if (handler instanceof Function) {
          this.emitter.removeListener(name, handler);
        }
      }
    }
  }

  public addEventListener<
    Event extends Extract<keyof EventMap, string>,
    Value extends EventMap[Event]
  >(name: Event, handler: (value: Value) => void) {
    this.emitter.addListener(name, handler);
  }

  public removeEventListener<
    Event extends Extract<keyof EventMap, string>,
    Value extends EventMap[Event]
  >(name: Event, handler: (event: Value) => void) {
    this.emitter.removeListener(name, handler);
  }
}
